/**
 * What a farm needs its owner to deal with, as pure functions.
 *
 * This lives outside the portal because the navbar badges the same number, and
 * pulling it from the portal would drag OrdersTab and the whole portal bundle
 * into every page a shopper loads.
 */

/** Produce at or below this is worth a nudge before it stops a basket. */
export const LOW_STOCK = 5;

/** Orders auto-cancel this long after they are placed if nobody accepts them. */
export const ACK_WINDOW_MS = 4 * 60 * 60 * 1000;

export const isPendingAck = (o) =>
  o?.status === "Placed" && !o.farmerAckedAt && !o.autoCancelledUtc;

/** How long the farmer has left to accept, or null once the window has passed. */
export const ackTimeLeft = (order, now = Date.now()) => {
  const placed = Date.parse(order?.createdUtc || "");
  if (Number.isNaN(placed)) return null;
  const left = placed + ACK_WINDOW_MS - now;
  if (left <= 0) return null;
  return {
    hours: Math.floor(left / 3600000),
    minutes: Math.floor((left % 3600000) / 60000),
  };
};

/**
 * Which ingredient is stopping a basket from being built, and by how much.
 * The API reports availableQuantity but not the reason, and "this cannot be
 * built" without a name is not something a farmer can act on.
 */
export const bottleneck = (basket, productsById) => {
  let worst = null;
  for (const item of basket?.items || []) {
    const product = productsById.get(Number(item.productId));
    const stock = Number(product?.stock);
    const per = Number(item.quantity);
    if (!per || per < 1 || !Number.isFinite(stock)) continue;
    const builds = Math.floor(stock / per);
    if (worst === null || builds < worst.builds) {
      worst = {
        builds,
        product,
        per,
        stock,
        name: product?.name || item.productName,
        // What it takes to build one more of this basket. Always >= 1, because
        // per * (builds + 1) is by definition above current stock.
        shortBy: Math.max(1, per * (builds + 1) - stock),
      };
    }
  }
  return worst;
};

/** How many whole copies of a basket current stock can build. */
export const buildableCount = (items, productsById) => {
  const usable = (items || []).filter((i) => Number(i.quantity) > 0);
  if (usable.length === 0) return null;
  let min = Infinity;
  for (const item of usable) {
    const stock = Number(productsById.get(Number(item.productId))?.stock);
    if (!Number.isFinite(stock)) return null;
    min = Math.min(min, Math.floor(stock / Number(item.quantity)));
  }
  return Number.isFinite(min) ? Math.max(0, min) : null;
};

/**
 * Everything that needs the farmer, in priority order. Orders lead because they
 * expire on their own; the rest degrade the shop quietly.
 */
export const computeFarmTasks = ({ orders = [], products = [], baskets = [] } = {}) => {
  const productsById = new Map(products.map((p) => [Number(p.id), p]));

  const pendingOrders = orders.filter(isPendingAck);

  const stuckBaskets = baskets
    .filter((b) => b.availableQuantity === 0)
    .map((b) => ({ basket: b, short: bottleneck(b, productsById) }))
    // A basket whose short ingredient isn't in the active catalogue gives the
    // farmer nothing to press, so it is left to the produce tab.
    .filter((entry) => entry.short);

  const active = products.filter((p) => p.isActive !== false);
  const outOfStock = active.filter((p) => Number(p.stock || 0) <= 0);
  const lowStock = active.filter((p) => {
    const stock = Number(p.stock || 0);
    return stock > 0 && stock <= LOW_STOCK;
  });

  return {
    productsById,
    pendingOrders,
    stuckBaskets,
    outOfStock,
    lowStock,
    taskCount:
      pendingOrders.length + stuckBaskets.length + outOfStock.length + lowStock.length,
  };
};
